import {useState} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {FileSpreadsheet, Globe, HandCoins, Loader2, Play, Receipt} from 'lucide-react';
import {DetailHeader} from '@internal/components/DetailHeader';
import {Tabs, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {Button} from '@common-web/components/ui/button';
import {trpc} from '@common-web/lib/trpc';
import {TenantTag} from '@internal/pages/group-pipeline/components/TenantTag';
import {VbaStatusIndicator} from '@internal/pages/group-pipeline/components/VbaStatusIndicator';
import {PhaseBadge} from '@internal/pages/group-pipeline/components/PhaseBadge';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {formatDate} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {StatusIndicator} from '@internal/pages/group-pipeline/components/StatusIndicator';
import {StartPipelineDialog} from '@internal/pages/group-pipeline/components/StartPipelineDialog';
import {NextMoveCard} from '@internal/pages/group-pipeline/detail/NextMoveCard';
import {GroupOverviewTab} from '@internal/pages/group-pipeline/detail/GroupOverviewTab';
import {GroupTasksTab} from '@internal/pages/group-pipeline/detail/GroupTasksTab';
import {
  GroupDocumentsTab,
  missingDocumentCount,
} from '@internal/pages/group-pipeline/detail/GroupDocumentsTab';
import {EmptyTabState} from '@internal/pages/group-pipeline/components/EmptyTabState';
import {useTaskActions} from '@internal/pages/group-pipeline/detail/useTaskActions';
import {
  GroupActivityTab,
  useGroupActivity,
} from '@internal/pages/group-pipeline/detail/GroupActivityTab';
import {StatusMenu} from '@internal/pages/group-pipeline/detail/StatusMenu';
import {NOT_BUILT, PreviewDot} from '@internal/pages/group-pipeline/components/PreviewDot';

const TABS = [
  'overview',
  'tasks',
  'census',
  'documents',
  'invoicing',
  'commissions',
  'enrollment-site',
  'activity',
] as const;
type DetailTab = (typeof TABS)[number];

const parseTab = (value: string | null): DetailTab =>
  TABS.includes(value as DetailTab) ? (value as DetailTab) : 'overview';

// Tenants whose groups don't get enrollment sites, by Client.vbaShortClientName (lowercased).
const NO_ENROLLMENT_SITE_TENANTS = new Set(['healthcues', 'quantum', 'champion health', 'synrgy']);

export function GroupPipelineDetailPage() {
  const {vbaGroupId = ''} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = parseTab(searchParams.get('tab'));

  // The list or board passes its own URL so Back keeps the user's filters.
  const backTo = (location.state as {from?: string} | null)?.from ?? '/group-pipeline';

  const {data: group, isLoading} = trpc.internal.groupPipeline.detail.useQuery({vbaGroupId});
  const activity = useGroupActivity(vbaGroupId);
  const activityTotal = activity.data?.pages[0]?.counts.all ?? 0;
  const taskActions = useTaskActions(vbaGroupId);
  const [startOpen, setStartOpen] = useState(false);

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, {replace: true, state: location.state});
  };

  const back = {backLabel: 'Back to pipeline', onBack: () => navigate(backTo)};

  if (isLoading) {
    return (
      <div className='py-24 text-center'>
        <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (!group) {
    return (
      <div className='mx-auto w-full max-w-7xl px-6 py-6'>
        <DetailHeader {...back} title='Group not found' />
        <p className='py-16 text-center text-sm text-muted-foreground'>
          That group could not be found.
        </p>
      </div>
    );
  }

  const pipeline = group.pipeline;
  const showEnrollmentSite = !NO_ENROLLMENT_SITE_TENANTS.has(
    group.tenant?.shortName.toLowerCase() ?? '',
  );
  const tab = requestedTab === 'enrollment-site' && !showEnrollmentSite ? 'overview' : requestedTab;
  const missingDocs = missingDocumentCount(pipeline);
  const openTaskCount = pipeline?.tasks.filter((t) => t.status === 'OPEN').length ?? 0;
  const showNextMove = Boolean(pipeline?.state.currentTask && !pipeline.offState);

  return (
    <div className='mx-auto w-full max-w-7xl space-y-4 px-6 py-6'>
      <DetailHeader
        {...back}
        title={group.name}
        titleExtra={
          <>
            <TenantTag shortName={group.tenant?.shortName ?? null} />
            <VbaStatusIndicator status={group.vbaStatus} />
            <PhaseBadge phase={group.phase} />
            {(group.status === 'ON_HOLD' || group.status === 'NOT_MOVING_FORWARD') && (
              <StatusIndicator
                status={group.status}
                context={{
                  stepLabel: pipeline?.state.currentTask?.label ?? null,
                  dueAt: pipeline?.state.currentTask?.dueAt ?? null,
                  offReason: pipeline?.offReason ?? null,
                }}
              />
            )}
          </>
        }
        subtitle={
          <span>
            {group.admin123AgentId && <>Agent ID {group.admin123AgentId} · </>}
            {group.source === 'ADMIN123' ? (
              <>
                VBA ID <PendingVbaId vbaGroupId={group.vbaGroupId} /> (pending)
              </>
            ) : (
              <>
                VBA ID {group.vbaGroupId} · {group.members.toLocaleString()} members · Effective{' '}
                <span className='font-semibold text-gray-900'>
                  {formatDate(group.effectiveDate)}
                </span>
              </>
            )}
          </span>
        }
        actions={
          pipeline
            ? taskActions.canManage &&
              !pipeline.liveAt && <StatusMenu vbaGroupId={group.vbaGroupId} pipeline={pipeline} />
            : taskActions.canManage && (
                <Button onClick={() => setStartOpen(true)}>
                  <Play className='mr-1.5 h-4 w-4' />
                  Add to pipeline
                </Button>
              )
        }
      />

      {showNextMove && pipeline && (
        <NextMoveCard
          pipeline={pipeline}
          canManage={taskActions.canManage}
          isPending={taskActions.isPending}
          onAction={taskActions.run}
        />
      )}

      <Tabs value={tab} onValueChange={setTab} noHash>
        <TabsList>
          <TabsTrigger value='overview' noHash>
            Overview
          </TabsTrigger>
          <TabsTrigger value='tasks' noHash className='gap-2'>
            Tasks
            {openTaskCount > 0 && (
              <span className='rounded-full bg-gray-200 px-1.5 text-xs font-semibold text-gray-700'>
                {openTaskCount} open
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value='census' noHash className='relative' title={NOT_BUILT}>
            Census
            <PreviewDot />
          </TabsTrigger>
          <TabsTrigger value='documents' noHash className='gap-2'>
            Documents
            {missingDocs > 0 && (
              <span className='rounded-full bg-gray-200 px-1.5 text-xs font-semibold text-gray-700'>
                {missingDocs} missing
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value='invoicing' noHash className='relative' title={NOT_BUILT}>
            Invoicing
            <PreviewDot />
          </TabsTrigger>
          <TabsTrigger value='commissions' noHash className='relative' title={NOT_BUILT}>
            Commissions
            <PreviewDot />
          </TabsTrigger>
          {showEnrollmentSite && (
            <TabsTrigger value='enrollment-site' noHash className='relative' title={NOT_BUILT}>
              Enrollment site
              <PreviewDot />
            </TabsTrigger>
          )}
          <TabsTrigger value='activity' noHash className='gap-2'>
            Activity
            {activityTotal > 0 && (
              <span className='rounded-full bg-gray-200 px-1.5 text-xs font-semibold text-gray-700'>
                {activityTotal}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'overview' && <GroupOverviewTab group={group} />}
      {tab === 'tasks' && (
        <GroupTasksTab
          group={group}
          onStartPipeline={() => setStartOpen(true)}
          canManage={taskActions.canManage}
          isPending={taskActions.isPending}
          onAction={taskActions.run}
        />
      )}
      <StartPipelineDialog
        open={startOpen}
        onOpenChange={setStartOpen}
        group={{
          vbaGroupId: group.vbaGroupId,
          name: group.name,
          tenantName: group.tenant?.shortName ?? null,
        }}
      />
      {taskActions.dialog}
      {tab === 'census' && (
        <EmptyTabState
          icon={FileSpreadsheet}
          title='Coming soon'
          description='Upload the census on Documents'
          action={
            <Button variant='outline' className='bg-white' onClick={() => setTab('documents')}>
              Go to Documents
            </Button>
          }
        />
      )}
      {tab === 'documents' && <GroupDocumentsTab group={group} canManage={taskActions.canManage} />}
      {tab === 'invoicing' && (
        <EmptyTabState icon={Receipt} title='No invoices yet' description='Starts at go-live' />
      )}
      {tab === 'commissions' && (
        <EmptyTabState
          icon={HandCoins}
          title='No commissions yet'
          description='Set up at go-live'
        />
      )}
      {tab === 'enrollment-site' && (
        <EmptyTabState
          icon={Globe}
          title='No enrollment site yet'
          description='Built during implementation'
        />
      )}
      {tab === 'activity' && <GroupActivityTab vbaGroupId={vbaGroupId} groupName={group.name} />}
    </div>
  );
}

// The ID the sync will create the group under, muted until VBA has it.
function PendingVbaId({vbaGroupId}: {vbaGroupId: string}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className='cursor-help text-gray-400 underline decoration-dotted underline-offset-[3px]'
        >
          {vbaGroupId}
        </span>
      </TooltipTrigger>
      <TooltipContent className='max-w-xs bg-gray-900 text-white'>
        Expected ID. The sync will create the group in VBA under this ID.
      </TooltipContent>
    </Tooltip>
  );
}
